// tslint:disable: no-console
import { NOTEBOOK_EDITOR_JL_FRAME_RENDER_TID } from "@kaggle/core";
import { featureFlags, Kaggle, reportEvent } from "@kaggle/sdk";
import styled, { ThemeName } from "@kaggle/styled-components";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import ServicesContext from "../../../api/ServicesContext";
import { getJupyterUrl } from "../../connector/extensions/utils/getJupyterUrl";
import { JupyterEvent } from "../../jupyter/types";
import { editorFrameClient as frameClient } from "../../pages/editor/editorFrameClient";
import { setJupyterStatus } from "../../redux/actions/jupyter";
import { showFatalErrorModal } from "../../redux/actions/modal";
import { saveIpynb } from "../../redux/actions/notebook";
import { createSetSessionStateAction } from "../../redux/actions/session";
import { setTerminalExpanded } from "../../redux/actions/terminal";
import { isStopped, startWorker } from "../../redux/actions/worker";
import { observeSessionState } from "../../redux/observables/observeSessionState";
import {
  getCurrentEditorStatus,
  getCurrentIpynb,
} from "../../redux/selectors/current";
import { getJupyterProxyUrl } from "../../redux/selectors/jupyter";
import { getCurrentJupyterKernelName } from "../../redux/selectors/kernelDraft";
import { getModalVisible } from "../../redux/selectors/modal";
import { getSessionState, getSourceType } from "../../redux/selectors/session";
import { getStore } from "../../redux/store/appStore";
import { JupyterStatus } from "../../redux/types";
import { editorQueue } from "../../redux/utils/editorSnackbarQueue";
import { EditorStatus } from "../../types/EditorStatus";

import EditorType = Kaggle.Sdk.Kernels.EditorType;

export const REDUX_ACTION_KEY = "REDUX_ACTION";
export const SAVE_IPYNB_ACTION_KEY = "SAVEIPYNB_ACTION";
export const EDITOR_READY_ACTION_KEY = "EDITOR_READY";
export const EDITOR_INIT_FINISHED_ACTION_KEY = "EDITOR_INIT_FINISHED";

const FrameContainer = styled.div<{ isModalVisible: boolean }>`
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;

  iframe {
    border: 0;
    flex: 1;

    ${p => p.isModalVisible && "pointer-events: none"};
  }
`;

export const SessionlessEditor = () => {
  const { useEffect } = React;
  const services = React.useContext(ServicesContext);
  const dispatch = useDispatch();
  const store = getStore();
  const frameContainerRef = React.useRef<HTMLDivElement>(null);
  const session = useSelector(getSessionState);
  const baseJupyterUrl = getJupyterUrl();
  const proxyUrl = useSelector(getJupyterProxyUrl);
  const kernelName = useSelector(getCurrentJupyterKernelName);
  const editorStatus = useSelector(getCurrentEditorStatus);
  const ipynb = useSelector(getCurrentIpynb);
  const isModalVisible = useSelector(getModalVisible);
  const initStarted = React.useRef<boolean>(false);

  useEffect(() => {
    if (!ipynb || initStarted.current) {
      return;
    }

    initStarted.current = true;

    if (!frameContainerRef.current) {
      throw new Error("No reference to the current editor frame container.");
    }

    if (!baseJupyterUrl) {
      throw new Error("Jupyter URL was not set.");
    }

    const isSessionStarting = !store.getState().notebook.startedCanceled;
    if (isSessionStarting) {
      editorQueue.notify({
        title: "Session is starting...",
        timeout: 4000,
        leading: true,
      });
    }

    // Conditionally passes a querystring parameter that will
    // un-disable the LSP autocomplete extension (b/211487924).
    //
    // NOTE: this will still be required even after LSP launches
    // since there will always be some kernels on older docker
    // containers that don't support LSP. In those cases, the iframe
    // should not enable LSP in order to allow Jupyter to fall back
    // to non-LSP autocomplete.
    const jupyterUrl = store.getState().enableLsp
      ? baseJupyterUrl + "&lsp=true"
      : baseJupyterUrl;

    const frameContainer = frameContainerRef.current;
    services.frameClient
      .createJupyterFrame(async () => {
        await frameClient.connect(frameContainer, jupyterUrl);

        frameClient.setContent(ipynb);

        let lastThemeName: ThemeName;
        let lastSourceType: Kaggle.Sdk.Kernels.EditorType;
        observeSessionState(store, ({ themeName, sourceType }) => {
          if (lastThemeName !== themeName) {
            frameClient.setTheme(themeName);
            lastThemeName = themeName;
          }
          if (lastSourceType !== sourceType) {
            frameClient.setSourceType(sourceType);
            lastSourceType = sourceType;
          }
        });

        frameClient.on(EDITOR_READY_ACTION_KEY, () => {
          dispatch(setJupyterStatus(JupyterStatus.RUNNING));

          // Immediately move to disconnected if the notebook is actually offline.
          // We need to move to RUNNING before this so that timeToEdit metrics
          // still properly report how long it took to load the editor.
          const disconnected = store.getState().notebook.startedCanceled;
          if (disconnected) {
            dispatch(setJupyterStatus(JupyterStatus.DISCONNECTED));
          }
        });

        frameClient.on(EDITOR_INIT_FINISHED_ACTION_KEY, () => {
          // Add a test attribute for e2e browser tests after bootstrap init
          // has completed.
          frameContainer.setAttribute(
            "data-testid",
            NOTEBOOK_EDITOR_JL_FRAME_RENDER_TID
          );
        });

        // Events emitted by the child to be handled by the parent.
        frameClient.on(REDUX_ACTION_KEY, dispatch);

        frameClient.on(SAVE_IPYNB_ACTION_KEY, async () => {
          await saveIpynb(() => {
            if (featureFlags.kernelsSaveCellOutput) {
              return frameClient.getRAWSourceWithOutputs();
            } else {
              return frameClient.getRAWSource();
            }
          });
        });

        const emptyMessageQueue = () => {
          editorQueue.messages.array.length = 0;
        };
        frameClient.on(JupyterEvent.CELL_DELETE, () => {
          editorQueue.notify({
            title: "Cell deleted",
            timeout: 5000,
            leading: true,
            actions: [
              {
                label: "Undo",
                onClick: () => {
                  reportEvent("kernels", "editor-cell-deleted-snackbar-undo");
                  frameClient.undoCellDelete();
                  // Because we can only undo the last deleted cell, there's
                  // not really a point to let users undo multiple times.
                  emptyMessageQueue();
                },
              },
              {
                label: "Dismiss",
                onClick: () => {
                  reportEvent(
                    "kernels",
                    "editor-cell-deleted-snackbar-dismiss"
                  );
                  emptyMessageQueue();
                },
              },
            ],
          });
        });

        frameClient.on(JupyterEvent.CODE_CELL_EXECUTE, () => {
          if (isStopped(store.getState())) {
            // Automatically start worker when stopped on cell execute.
            startWorker();
          }

          const sourceType = getSourceType(store.getState());
          if (sourceType === EditorType.SCRIPT) {
            setTerminalExpanded(true);
          }
        });

        frameClient.dispatch(createSetSessionStateAction(session));

        return frameClient.frame;
      })
      .then(() => frameClient.initialize());
  }, [ipynb]);

  useEffect(() => {
    services.frameClient.publishSessionState(session);
  }, [session]);

  useEffect(() => {
    if (!frameContainerRef.current) {
      throw new Error("No reference to the current editor frame container.");
    }

    frameContainerRef.current.style.display =
      editorStatus === EditorStatus.QUEUED ? "none" : "flex";

    switch (editorStatus) {
      case EditorStatus.FATAL:
        frameClient.lockEditor();
        showFatalErrorModal();
        break;
    }
  }, [editorStatus]);

  useEffect(() => {
    if (!proxyUrl) {
      return;
    }
    if (!kernelName) {
      throw new Error("Kernel name was not set.");
    }
    frameClient.connectKernel(proxyUrl, kernelName);
  }, [proxyUrl]);

  return (
    <FrameContainer ref={frameContainerRef} isModalVisible={isModalVisible} />
  );
};
